/* eslint-disable @typescript-eslint/no-explicit-any */
import { Inject, Injectable } from '@angular/core';
import { ApplicationInsights, IExceptionTelemetry } from '@microsoft/applicationinsights-web';
import { SHARED_ENVIRONMENT, SharedEnvironment } from '@sd-portal/shared/environment';

@Injectable({
  providedIn: 'root'
})
export class AppMonitoringService {
  private readonly appInsights: ApplicationInsights;

  constructor(@Inject(SHARED_ENVIRONMENT) private readonly environment: SharedEnvironment) {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.appInsightsConfig.instrumentationKey
      }
    });

    this.appInsights.loadAppInsights();
    this.appInsights.trackPageView(); // manually call trackPageView to establish the current user/session/pageview

    // Setup custom app insights telemetry
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.appInsights.addTelemetryInitializer((envelope: any) => {
      envelope.tags['ai.cloud.role'] = this.environment.appInsightsConfig.cloudRoleName;
    });
  }

  logPageView = (name?: string, url?: string) => {
    // option to manually call trackPageView to establish the current user/session/pageview
    this.appInsights.trackPageView({
      name: name,
      uri: url
    });
  };

  logEvent = (name: string, properties?: { [key: string]: any }) =>
    this.appInsights.trackEvent({ name: name }, properties);

  logMetric = (name: string, average: number, properties?: { [key: string]: any }) =>
    this.appInsights.trackMetric({ name: name, average: average }, properties);

  logException = (exception: Error, severityLevel?: number) => {
    const exceptionTelemetry: IExceptionTelemetry = {
      exception: exception,
      severityLevel: severityLevel
    };

    if (this.appInsights.context.user) {
      const customTelemetry = {
        customerId: this.appInsights.context.user.accountId,
        userId: this.appInsights.context.user.authenticatedId
      };

      this.appInsights.trackException(exceptionTelemetry, customTelemetry);
    } else {
      this.appInsights.trackException(exceptionTelemetry);
    }
  };

  logTrace = (message: string, properties?: { [key: string]: any }) =>
    this.appInsights.trackTrace({ message: message }, properties);

  setAuthenticatedUserContext = (authenticatedUserId: string, accountId?: string | undefined) =>
    this.appInsights.setAuthenticatedUserContext(authenticatedUserId, accountId);

  clearAuthenticatedUserContext = () => this.appInsights.clearAuthenticatedUserContext();
}
